/**
 * WebXR — walking the fab in VR.
 *
 * The proposal is specific about what immersion is for, and it is not novelty.
 * At the macroscopic scale it makes protocol embodied: the student stands in a
 * space with real dimensions, walks the line, and reads a gauge at the height
 * it is actually mounted at. Spatial and procedural memory engage in a way a
 * desktop view does not reach.
 *
 * Two design decisions follow from that, and both are constraints rather than
 * features.
 *
 * IT IS OPTIONAL, ALWAYS. Everything the curriculum needs is reachable on an
 * ordinary classroom computer. VR adds embodiment to the walking; it gates
 * nothing. If no headset is present the button simply never appears.
 *
 * THE TOOL PANELS STAY ON THE DESKTOP. The instrument panels are dense DOM -
 * charts, sliders, transcripts - and pushing that into a headset at current
 * text resolution would make the physics harder to read, not easier. So VR is
 * for the floor: walking it, standing at a tool, seeing the chamber at scale.
 * Opening a tool drops back to the desktop panel deliberately.
 */

import * as THREE from 'three'

/** Metres per second of smooth locomotion under full stick deflection. */
const MOVE_SPEED = 2.4
/** Radians per snap turn. */
const SNAP_ANGLE = Math.PI / 6
/** Stick deflection below which nothing happens. */
const DEADZONE = 0.15

export class VRMode {
  /** True once an immersive-vr session is actually running. */
  active = false

  private session: XRSession | null = null
  private controllers: THREE.Group[] = []
  private snapArmed = true
  /** Eye height the desktop rig sits at, restored when the session ends. */
  private desktopEyeHeight = 1.68

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly rig: THREE.Object3D,
    private readonly scene: THREE.Scene,
  ) {
    renderer.xr.enabled = true
    // 'local-floor' puts the origin on the physical floor, so a 1.7 m student
    // and a 1.5 m student each see the fab from their own real eye height
    // rather than from an assumed one. That is the whole point of doing this
    // in a headset.
    renderer.xr.setReferenceSpaceType('local-floor')
    this.buildControllers()
  }

  /** Is an immersive headset actually available on this machine? */
  static async isSupported(): Promise<boolean> {
    const xr = (navigator as Navigator & { xr?: XRSystem }).xr
    if (!xr?.isSessionSupported) return false
    try {
      return await xr.isSessionSupported('immersive-vr')
    } catch {
      return false
    }
  }

  private buildControllers(): void {
    // A short ray from each hand. Not for picking - just so the student can see
    // where their hands are, which is most of what stops VR feeling untethered.
    const geometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, -0.4),
    ])
    const material = new THREE.LineBasicMaterial({ color: 0x4ac6ff, transparent: true, opacity: 0.6 })

    for (let i = 0; i < 2; i++) {
      const controller = this.renderer.xr.getController(i)
      controller.add(new THREE.Line(geometry, material))
      // Controllers live under the rig so they move with the student.
      this.rig.add(controller)
      this.controllers.push(controller)
    }
  }

  async enter(): Promise<void> {
    const xr = (navigator as Navigator & { xr?: XRSystem }).xr
    if (!xr) return
    const session = await xr.requestSession('immersive-vr', {
      optionalFeatures: ['local-floor', 'bounded-floor'],
    })
    this.session = session
    await this.renderer.xr.setSession(session)

    // The headset supplies eye height from the floor, so the rig has to sit ON
    // the floor. Leaving it at desktop eye height would put the student's head
    // 1.7 m above their own head.
    this.desktopEyeHeight = this.rig.position.y
    this.rig.position.y = 0
    this.active = true

    session.addEventListener('end', () => {
      this.active = false
      this.session = null
      this.rig.position.y = this.desktopEyeHeight
    })
  }

  exit(): void {
    this.session?.end()
  }

  /**
   * Controller locomotion, run once per frame while a session is live.
   *
   * Left stick moves relative to where the student is LOOKING, not where the
   * rig is facing - anything else is immediately disorienting. Right stick
   * snap-turns rather than smooth-turns, because smooth yaw is the single
   * biggest cause of simulator sickness and a classroom cannot afford it.
   */
  update(dt: number, collide: (pos: THREE.Vector3) => void): void {
    if (!this.active || !this.session) return

    let moveX = 0
    let moveY = 0
    let turn = 0

    for (const source of this.session.inputSources) {
      const axes = source.gamepad?.axes
      if (!axes || axes.length < 4) continue
      // Standard XR mapping puts the thumbstick on axes 2 and 3.
      const x = axes[2] ?? 0
      const y = axes[3] ?? 0
      if (source.handedness === 'left') {
        if (Math.abs(x) > DEADZONE) moveX += x
        if (Math.abs(y) > DEADZONE) moveY += y
      } else if (source.handedness === 'right') {
        if (Math.abs(x) > DEADZONE) turn += x
      }
    }

    // --- snap turn, one step per stick push ---
    if (Math.abs(turn) > 0.7) {
      if (this.snapArmed) {
        this.rig.rotation.y -= Math.sign(turn) * SNAP_ANGLE
        this.snapArmed = false
      }
    } else if (Math.abs(turn) < 0.3) {
      this.snapArmed = true
    }

    // --- smooth locomotion in the direction of gaze ---
    if (moveX !== 0 || moveY !== 0) {
      const camera = this.renderer.xr.getCamera()
      const forward = new THREE.Vector3()
      camera.getWorldDirection(forward)
      forward.y = 0
      if (forward.lengthSq() < 1e-6) return
      forward.normalize()
      const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize()

      const next = this.rig.position.clone()
      next.addScaledVector(forward, -moveY * MOVE_SPEED * dt)
      next.addScaledVector(right, moveX * MOVE_SPEED * dt)
      // Same colliders as the desktop walk, so a wall is a wall in both.
      collide(next)
      this.rig.position.x = next.x
      this.rig.position.z = next.z
    }
  }

  /** Anything the session added to the scene, for teardown. */
  dispose(): void {
    for (const c of this.controllers) this.rig.remove(c)
    this.controllers = []
    void this.scene
  }
}
